import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const isIdSegment = (value) => {
  if (typeof value !== 'string') return false;
  if (value === '[object Object]') return true;
  return /^[0-9a-fA-F]{24}$/.test(value) || /^\d+$/.test(value);
};

/** URL segmenti; breadcrumb'da gösterilmez (yalnızca path birikir). */
const SKIP_BREADCRUMB_SEGMENTS = new Set(['tech-service']);

const CONTENT_TYPE_LABELS = {
  urun: 'Ürün Yönetimi',
  parca: 'Parça Yönetimi',
  islem: 'İşlem Yönetimi',
  ariza: 'Arıza Yönetimi',
  gereksinimler: 'Gereksinimler',
  'bakim-yonetimi': 'Bakım Yönetimi',
};

const ROUTE_LABELS = {
  'tech-services': 'Teknik Servis Yönetimi',
  bayiler: 'Bayi Yönetimi',
  'teknik-servisler': 'Teknik Servisler',
  'il-etiketleri': 'Harita etiket konumları',
  users: 'Kullanıcı Yönetimi',
  kullanicilar: 'Kullanıcı Yönetimi',
  categories: 'Kategoriler Yönetimi',
  contents: 'İçerikler Yönetimi',
  icerik: 'İçerik Yönetimi',
  parca: 'Parça Yönetimi',
  islem: 'İşlem Yönetimi',
  gereksinimler: 'Gereksinimler Yönetimi',
  'training-videos': 'Eğitimler',
  customers: 'Müşteri Yönetimi',
  'service-records': 'Servis Kaydı Takip',
  'complaint-records': 'İstek Şikayet Takibi',
  'gorusme-kayitlari': 'Görüşme Kayıtları',
  'bayilik-onaylari': 'Bayilik Onayları',
  'cihaz-sorgulama': 'Cihaz Sorgulama',
  'completed-appointments': 'Randevular',
  appointments: 'Randevular',
  teknisyenler: 'Teknisyenler',
  teknisyen: 'Teknisyen Paneli',
  'cari-hesaplar': 'Cari Hareketler',
  'randevu-bedelleri': 'Randevu Bedelleri',
  'stok-yonetimi': 'Stok Yönetimi',
  'stok-kullanim': 'Stok Yönetimi',
  'stok-ara': 'Stok Ara',
  'teknik-servis-stoklari': 'Teknik Servis Stokları',
  hakedisler: 'Hakedişler',
  yapilacaklar: 'Yapılacaklar',
  'db-yedekleme': 'DB Yedekleme',
  'test-veri-temizle': 'Test verilerini sil',
  'egitim-ts-kayitlari': 'Eğitim TS kayıtları',
  istatistik: 'İstatistik',
  cihazlar: 'Cihazlar',
  ariza: 'Arıza İstatistiği',
  'siparis-takip': 'Sipariş Takip Sistemi',
  siparisler: 'Sipariş Sistemi',
  'iade-kargolari': 'İade Kargoları',
  'iade-faturalari': 'İade Faturaları',
  'cihaz-degisimi': 'Cihaz Değişimi Yönetimi',
  gonderimler: 'Gönderimler',
  duyurular: 'Duyurular',
  'hesap-oturumlar': 'Aktif Oturumlar',
  ayarlar: 'Ayarlar',
  'km-ayarlari': 'KM Ayarları',
  'iskonto-ayari': 'İskonto Ayarı',
  dosyalar: 'Dosyalar',
  'sisteme-randevu-test': 'Sisteme Randevu Test',
  'apk-surumleri': 'APK Sürümleri',
  'test-panel': 'SAP',
  sms: 'SMS Yönetimi',
  checklists: 'Checklistler',
  'randevu-islemleri': 'Randevu İşlemleri',
  'randevu-test': 'Randevu Test',
  urun: 'Ürün Yönetimi',
};

const STOK_YONETIMI_TAB_LABELS = {
  'stok-ara': 'Stok Arama',
  'teknik-servis-stoklari': 'Teknik Servis Stokları',
};

const getPrevNonIdPart = (parts, index) => {
  for (let j = index - 1; j >= 0; j -= 1) {
    if (!isIdSegment(parts[j])) {
      return parts[j];
    }
  }
  return null;
};

const shouldSkipSegment = (part, parts, index) => {
  if (SKIP_BREADCRUMB_SEGMENTS.has(part)) return true;
  if (part === 'contents' && CONTENT_TYPE_LABELS[parts[index + 1]]) return true;
  if (part === 'teknisyen' && parts[index + 1] && !isIdSegment(parts[index + 1])) return true;
  if (part === 'teknik-servis' && getPrevNonIdPart(parts, index) === 'cari-hesaplar') return true;
  if (part === 'appointments' && isIdSegment(parts[index + 1])) return true;
  return false;
};

const resolveRouteLabel = (part, { path, parts, index, activeTab }) => {
  const prevNonIdPart = getPrevNonIdPart(parts, index);
  const isUnderTechService = path.startsWith('/tech-service');

  if (prevNonIdPart === 'contents' && CONTENT_TYPE_LABELS[part]) {
    return CONTENT_TYPE_LABELS[part];
  }

  if (prevNonIdPart === 'icerik' && CONTENT_TYPE_LABELS[part]) {
    return CONTENT_TYPE_LABELS[part];
  }

  if (part === 'service-records') {
    return isUnderTechService ? 'Servis Kayıtları' : 'Servis Kaydı Takip';
  }

  if (part === 'duyurular') {
    return isUnderTechService ? 'Mesajlar' : 'Duyurular';
  }

  if (part === 'cihaz-degisimi') {
    return isUnderTechService ? 'Cihaz Değişimi' : 'Cihaz Değişimi Yönetimi';
  }

  if (part === 'create' && prevNonIdPart === 'service-records') {
    return activeTab || window.activeServiceRecordTab || 'Müşteri Arama';
  }

  if (part === 'update' && prevNonIdPart === 'service-records') {
    return 'Servis Kaydı Yönetimi';
  }

  if (part === 'edit') {
    if (prevNonIdPart === 'tech-services') return 'Teknik Servisi Düzenle';
    if (prevNonIdPart === 'users') return 'Kullanıcı Düzenle';
    if (prevNonIdPart === 'customers') return 'Müşteri Düzenle';
    return 'Düzenle';
  }

  if (part === 'add') {
    if (prevNonIdPart === 'tech-services') return 'Teknik Servis Ekle';
    if (prevNonIdPart === 'customers') return 'Müşteri Ekle';
    return 'Ekle';
  }

  if (ROUTE_LABELS[part]) {
    return ROUTE_LABELS[part];
  }

  return part
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Breadcrumb = ({ items = [], serviceName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const isServiceRecordCreatePage = location.pathname.includes('/service-records/create');
    if (isServiceRecordCreatePage) {
      const updateActiveTab = () => {
        const tab = window.activeServiceRecordTab || null;
        setActiveTab(tab);
      };

      updateActiveTab();
      const interval = setInterval(updateActiveTab, 100);
      return () => clearInterval(interval);
    }
    setActiveTab(null);
    return undefined;
  }, [location.pathname]);

  const breadcrumbItems = items.length > 0 ? items : (() => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);

    const items = [{ label: 'Anasayfa', path: '/', icon: 'fas fa-home' }];

    if (parts.length > 0) {
      let currentPath = '';

      parts.forEach((part, index) => {
        if (isIdSegment(part)) {
          currentPath += `/${part}`;
          return;
        }

        currentPath += `/${part}`;

        if (shouldSkipSegment(part, parts, index)) {
          return;
        }

        let label = resolveRouteLabel(part, { path, parts, index, activeTab });

        if (part === 'create' && getPrevNonIdPart(parts, index) === 'service-records') {
          const serviceRecordItem = items.find((item) => item.path === currentPath.replace('/create', ''));
          if (serviceRecordItem) {
            serviceRecordItem.label = 'Servis Kaydı Takip';
          }
        }

        if (label === 'Teknik Servisi Düzenle' && serviceName) {
          label = `Teknik Servisi Düzenle - ${serviceName.toUpperCase()}`;
        }

        items.push({
          label,
          path: currentPath,
          icon: null,
        });
      });

      if (/^\/tech-service\/teknisyenler\/[^/]+$/.test(path) && location.state?.technicianName) {
        items.push({
          label: location.state.technicianName,
          path,
          icon: null,
        });
      }

      if (/^\/appointments\/[^/]+$/.test(path) && location.state?.appointmentLabel) {
        items.push({
          label: location.state.appointmentLabel,
          path,
          icon: null,
        });
      }

      if (/^\/service-records\/[^/]+\/appointments\/[^/]+$/.test(path)) {
        items.push({
          label: 'Randevu Detayı',
          path,
          icon: null,
        });
      }

      if (path === '/stok-yonetimi') {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        const tabLabel = STOK_YONETIMI_TAB_LABELS[tab];
        if (tabLabel) {
          items.push({ label: tabLabel, path: `/stok-yonetimi?tab=${tab}`, icon: null });
        }
      }
    }

    return items;
  })();

  const handleClick = (path, isLast) => {
    if (!isLast && path) {
      navigate(path);
    }
  };

  return (
    <nav className="breadcrumb" aria-label="breadcrumb">
      <ol className="breadcrumb-list">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          return (
            <li key={index} className="breadcrumb-item">
              {index > 0 && <span className="breadcrumb-separator">/</span>}
              {item.icon ? (
                <button
                  type="button"
                  className="breadcrumb-link"
                  onClick={() => handleClick(item.path, isLast)}
                  disabled={isLast}
                  aria-label={item.label}
                >
                  <i className={item.icon}></i>
                </button>
              ) : (
                <button
                  type="button"
                  className={`breadcrumb-link ${isLast ? 'active' : ''}`}
                  onClick={() => handleClick(item.path, isLast)}
                  disabled={isLast}
                >
                  {item.label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
